/* eslint-disable complexity */
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PhoneInput from 'react-phone-input-2';
import classNames from 'classnames';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';
import { Select, Option as SelectOption } from 'carbon-react/lib/components/select';
import { Checkbox } from 'carbon-react/lib/components/checkbox';
import Typography from 'carbon-react/lib/components/typography';
import Link from 'carbon-react/lib/components/link';
import Box from 'carbon-react/lib/components/box/box.component';
import Button from 'carbon-react/lib/components/button';

import {
  BUSINESS_TYPE_ACCOUNTANCY_ID,
  BUSINESS_TYPE_BOOKKEEPER_ID,
  BUSINESS_TYPE_BUSINESS_ID,
  businessTypeOptions
} from '@futrli/shared-services';

import * as styles from '../onboarding.scss';
import { useLocalisedConstantsContext } from '../../../../../localisation/src/core/localised-constants-context';
import * as inputStyles from '../../../../../auth/src/core/common.scss';

enum FORM_FIELDS {
  businessPhoneNumber = 'businessPhoneNumber',
  businessType = 'businessType'
}

const MIN_PHONE_NUMBER_LENGTH = 10;

interface Props {
  submitFormDisabled: boolean;
  isCanadaOrUs: boolean;
  businessPhoneNumber: string;
  handlePhoneNumberChange: (newPhoneNumber: string) => void;
  countryTouched: boolean;
  phoneNumberTouched: boolean;
  businessType: string;
  handleBusinessTypeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readPrivacy: boolean;
  handleReadPrivacyClick: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  receiveMarketingEmails: boolean | null;
  handleReceiveMarketingEmailsClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedCountry: string | null;
  handleCountryChange: (country: string | null) => void;
  handleNextClick: () => Promise<void>;
  noTopPadding?: boolean;
  noSubmitButton?: boolean;
}

export const AdditionalInfoForm = ({
  submitFormDisabled,
  isCanadaOrUs,
  businessPhoneNumber,
  handlePhoneNumberChange,
  countryTouched,
  phoneNumberTouched,
  businessType,
  handleBusinessTypeChange,
  readPrivacy,
  handleReadPrivacyClick,
  receiveMarketingEmails,
  handleReceiveMarketingEmailsClick,
  selectedCountry,
  handleCountryChange,
  handleNextClick,
  noTopPadding = false,
  noSubmitButton = false
}: Props) => {
  const intl = useIntl();
  const { countriesList } = useLocalisedConstantsContext();

  const businessTypeNames: Record<string, string> = useMemo(
    () => ({
      [BUSINESS_TYPE_ACCOUNTANCY_ID]: intl.formatMessage({
        id: 'register.accountancy-firm',
        defaultMessage: 'Accountancy Firm'
      }),
      [BUSINESS_TYPE_BOOKKEEPER_ID]: intl.formatMessage({ id: 'register.bookkeeper', defaultMessage: 'Bookkeeper' }),
      [BUSINESS_TYPE_BUSINESS_ID]: intl.formatMessage({ id: 'register.business', defaultMessage: 'Business' })
    }),
    [intl]
  );

  const onCountryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleCountryChange(event.target.value);
    },
    [handleCountryChange]
  );

  return (
    <>
      <div className={styles.container} data-testid="SageCarbonAdditionalInfoForm">
        {!noTopPadding && <div style={{ height: 200 }} />}
        <Typography variant="h1" fontSize="32px" fontWeight="700" mb="10px">
          <FormattedMessage
            id="onboarding.we_need_more_information"
            defaultMessage="We need a little more information."
          />
        </Typography>
        <Typography fontSize="24px" mb="20px">
          <FormattedMessage
            id="onboarding.for_personalisation_contact"
            defaultMessage="For personalisation, to be able to contact you and to make sure we’re data compliant."
          />
        </Typography>
        <FilterableSelect
          name="simple"
          id="simple"
          label={intl.formatMessage({
            id: 'common.country',
            defaultMessage: 'Country'
          })}
          placeholder={intl.formatMessage({
            id: 'register.start_typing_find_country',
            defaultMessage: 'Start typing to find your country'
          })}
          size="large"
          value={selectedCountry || ''}
          onChange={onCountryChange}
          error={
            countryTouched && !selectedCountry
              ? intl.formatMessage({
                  id: 'validation.field_required',
                  defaultMessage: 'This field is required.'
                })
              : undefined
          }
          mb="20px"
          maxWidth="750px"
        >
          {countriesList.map((c: string, i: number) => (
            <Option key={`${c}-${i}`} id={`${c}-${i}`} text={c} value={c} />
          ))}
        </FilterableSelect>
        <Typography fontSize="14px" mb="8px" fontWeight="500">
          <FormattedMessage id="register.business_telephone_number" defaultMessage="Business telephone number" />
        </Typography>
        <PhoneInput
          inputStyle={{ fontSize: 14 }}
          inputClass={classNames(inputStyles.phoneInput, inputStyles.xeroAdditionalPhoneInput, {
            [inputStyles.phoneInputError]: phoneNumberTouched && businessPhoneNumber.length < MIN_PHONE_NUMBER_LENGTH
            // [inputStyles.phoneInputDisabled]: isLoading
          })}
          inputProps={{
            name: FORM_FIELDS.businessPhoneNumber,
            required: true
          }}
          buttonClass={inputStyles.phoneButtonClass}
          dropdownClass={inputStyles.dropdownClass}
          dropdownStyle={{ width: 750 }}
          disableDropdown={false}
          value={businessPhoneNumber}
          onChange={(value: string) => handlePhoneNumberChange(value)}
          country={'gb'}
          preferredCountries={['gb', 'au', 'nz', 'ca', 'us']}
          preserveOrder={['preferredCountries']}
          isValid={() => {
            if (phoneNumberTouched) {
              return true;
            }
            if (!businessPhoneNumber) {
              return false;
            }
            return true;
          }}
        />
        {phoneNumberTouched && businessPhoneNumber.length < MIN_PHONE_NUMBER_LENGTH && (
          <Typography color="errorRed" mb={0} fontSize="10px" className={inputStyles.phoneNumberHelperCarbon}>
            <FormattedMessage
              id="validation.invalid_phone_number"
              defaultMessage="Please make sure you have entered a valid phone number."
            />
          </Typography>
        )}
        <Select
          label={intl.formatMessage({
            id: 'register.business_type',
            defaultMessage: 'Business type'
          })}
          value={businessType}
          onChange={handleBusinessTypeChange}
          name={FORM_FIELDS.businessType}
          size="large"
          maxWidth="750px"
          mb="20px"
          placeholder={intl.formatMessage({ id: 'common.predict.please_select', defaultMessage: 'Please select...' })}
        >
          {businessTypeOptions.map(o => (
            <SelectOption key={o.id} text={businessTypeNames[o.id]} value={o.id} />
          ))}
        </Select>
        <Checkbox
          checked={readPrivacy}
          onChange={handleReadPrivacyClick}
          size="large"
          value={readPrivacy ? 'true' : 'false'}
          label={
            <Typography fontWeight="400" fontSize="16px" mb={0}>
              <FormattedMessage
                id="register.agreed_terms_privacy"
                defaultMessage="I have read and accepted the {termsAndConditions}."
                values={{
                  termsAndConditions: (
                    <Link
                      href="https://www.futrli.com/terms"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.sageCarbonLink}
                    >
                      <FormattedMessage id="register.terms_and_conditions" defaultMessage="terms and conditions" />
                    </Link>
                  )
                }}
              />
            </Typography>
          }
          mb="20px"
          mt="4px"
        />
        <Box mb="20px" maxWidth="750px">
          {isCanadaOrUs ? (
            <Typography fontSize="16px" mb={0}>
              <FormattedMessage
                id="register.receive_emails_us_canada"
                defaultMessage="By submitting this form, you agree to be contacted with marketing communications."
              />
            </Typography>
          ) : (
            <Typography fontSize="16px" mb={0}>
              <FormattedMessage
                id="register.receive_emails_not_us_canada"
                defaultMessage="We would like to use the contact details you have provided above to send you Sage marketing emails,
                              and you can opt out at any time. Please see our {privacyNotice} for details of how we use your personal data."
                values={{
                  privacyNotice: (
                    <Link
                      href="https://sage.com/sageglobalprivacy"
                      target="_blank"
                      rel="noreferrer"
                      className={styles.sageCarbonLink}
                    >
                      <FormattedMessage id="register.privacy_notice" defaultMessage="Privacy Notice" />
                    </Link>
                  )
                }}
              />
            </Typography>
          )}
        </Box>
        <Checkbox
          value="true"
          onChange={handleReceiveMarketingEmailsClick}
          checked={receiveMarketingEmails !== null && receiveMarketingEmails}
          size="large"
          label={
            <Typography fontWeight="400" fontSize="16px" mb={0}>
              <FormattedMessage id="register.yes_please" defaultMessage="Yes please" />
            </Typography>
          }
          mb="20px"
        />
        <Checkbox
          value="false"
          onChange={handleReceiveMarketingEmailsClick}
          checked={receiveMarketingEmails !== null && !receiveMarketingEmails}
          size="large"
          label={
            <Typography fontWeight="400" fontSize="16px" mb={0}>
              <FormattedMessage id="register.no_thanks" defaultMessage="No thanks" />
            </Typography>
          }
          mb="30px"
        />
        {!noSubmitButton && (
          <Button
            className={styles.nextButton}
            onClick={handleNextClick}
            disabled={submitFormDisabled}
            buttonType="primary"
          >
            <FormattedMessage id="common.next" defaultMessage="Next" />
          </Button>
        )}
      </div>
    </>
  );
};
